import { useEffect, useState, useRef, useCallback } from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";

// ── Bancos Venezuela ──────────────────────────────────────────────────────────
const BANCOS = [
  { codigo: "0102", nombre: "Banco de Venezuela" },
  { codigo: "0105", nombre: "Mercantil" },
  { codigo: "0108", nombre: "BBVA Provincial" },
  { codigo: "0114", nombre: "Bancaribe" },
  { codigo: "0115", nombre: "Banco Exterior" },
  { codigo: "0116", nombre: "Occidental de Descuento (BOD)" },
  { codigo: "0128", nombre: "Banco Caroní" },
  { codigo: "0134", nombre: "Banesco" },
  { codigo: "0137", nombre: "Sofitasa" },
  { codigo: "0138", nombre: "Banco Plaza" },
  { codigo: "0146", nombre: "Bangente" },
  { codigo: "0149", nombre: "BANPLUS" },
  { codigo: "0151", nombre: "BFC Banco Fondo Común" },
  { codigo: "0156", nombre: "100% Banco" },
  { codigo: "0163", nombre: "Banco del Tesoro" },
  { codigo: "0166", nombre: "Banco Agrícola de Venezuela" },
  { codigo: "0168", nombre: "Bancrecer" },
  { codigo: "0171", nombre: "Banco Activo" },
  { codigo: "0172", nombre: "Bancamiga" },
  { codigo: "0175", nombre: "Banco Bicentenario" },
  { codigo: "0177", nombre: "Banfanb" },
  { codigo: "0191", nombre: "BNC Nacional de Crédito" },
];

// ── Definición de planes (display) ───────────────────────────────────────────
const PLANES = [
  {
    id: "gratis",
    label: "Gratis",
    colorBorde: "#d1d5db",
    colorBadge: "#f3f4f6",
    colorTexto: "#374151",
    features: [
      "50 productos",
      "10 categorías",
      "1 usuario",
      "500 visitas/mes",
      "2 plantillas básicas",
      "Pedidos por WhatsApp",
      "Marca de agua TuCatálogo",
    ],
    noFeatures: ["Exportar CSV", "Envío de promociones", "Soporte prioritario"],
  },
  {
    id: "emprendedor",
    label: "Emprendedor",
    colorBorde: "#6366f1",
    colorBadge: "#eef2ff",
    colorTexto: "#4338ca",
    popular: true,
    features: [
      "200 productos",
      "Categorías ilimitadas",
      "15 usuarios",
      "5.000 visitas/mes",
      "Todas las plantillas",
      "Sin marca de agua",
      "Exportar CSV",
      "Envío de promociones",
      "Soporte prioritario",
    ],
    noFeatures: ["Dominio propio", "Soporte dedicado"],
  },
  {
    id: "empresa",
    label: "Empresa",
    colorBorde: "#7c3aed",
    colorBadge: "#faf5ff",
    colorTexto: "#7c3aed",
    features: [
      "Productos ilimitados",
      "Categorías ilimitadas",
      "Usuarios ilimitados",
      "Visitas ilimitadas",
      "Todas las plantillas",
      "Sin marca de agua",
      "Exportar CSV",
      "Envío de promociones",
      "Dominio propio",
      "Soporte dedicado",
    ],
    noFeatures: [],
  },
];

// ── Componentes pequeños ──────────────────────────────────────────────────────

function BarraUso({ label, usado, limite, color }) {
  const porcentaje = limite ? Math.min((usado / limite) * 100, 100) : 0;
  const enLimite = limite && usado >= limite;
  const cercaDelLimite = limite && porcentaje >= 80 && !enLimite;
  const colorBarra = enLimite ? "#ef4444" : cercaDelLimite ? "#f59e0b" : color;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className={`font-semibold ${enLimite ? "text-red-600" : cercaDelLimite ? "text-amber-600" : "text-gray-800"}`}>
          {usado}{limite ? ` / ${limite}` : " / ilimitados"}
        </span>
      </div>
      {limite ? (
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${porcentaje}%`, background: colorBarra }} />
        </div>
      ) : (
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div className="h-2.5 rounded-full bg-green-400 w-full opacity-40" />
        </div>
      )}
      {enLimite && <p className="text-xs text-red-600 font-medium">Límite alcanzado — actualiza tu plan para agregar más</p>}
      {cercaDelLimite && <p className="text-xs text-amber-600">Cerca del límite ({Math.round(porcentaje)}% usado)</p>}
    </div>
  );
}

function Spinner() {
  return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block" />;
}

function AlertaError({ mensaje }) {
  if (!mensaje) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
      <span className="flex-shrink-0 mt-0.5">⚠</span>
      <span>{mensaje}</span>
    </div>
  );
}

// ── Modal overlay ─────────────────────────────────────────────────────────────

function Modal({ titulo, onCerrar, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
          <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Paso 1: Formulario de método de pago ─────────────────────────────────────

function FormMetodoPago({ metodoExistente, onUsar, onRegistrar, procesando, error, soloGuardar = false }) {
  const editando = !!metodoExistente;
  const [form, setForm] = useState({
    banco_codigo:   metodoExistente?.banco_codigo  || "",
    cuenta_tipo:    metodoExistente?.cuenta_tipo   || "CELE",
    cuenta_numero:  "",
    doc_tipo:       "V",
    doc_numero:     "",
    titular_nombre: metodoExistente?.titular_nombre || "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onRegistrar(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {editando ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Puedes modificar cualquier campo. Por seguridad, debes reingresar el número de cuenta y la cédula.
        </div>
      ) : (
        <p className="text-sm text-gray-600">
          Registra tu cuenta bancaria venezolana para el débito inmediato. Tus datos se guardan cifrados y nunca se comparten.
        </p>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
        <select
          value={form.banco_codigo}
          onChange={(e) => set("banco_codigo", e.target.value)}
          required
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
        >
          <option value="">Selecciona tu banco</option>
          {BANCOS.map((b) => (
            <option key={b.codigo} value={b.codigo}>{b.nombre}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de cuenta</label>
        <div className="flex gap-3">
          {[{ v: "CELE", l: "Teléfono / Pago móvil" }, { v: "CNTA", l: "Cuenta bancaria" }].map(({ v, l }) => (
            <label key={v} className={`flex-1 flex items-center gap-2 border rounded-lg px-3 py-2.5 cursor-pointer text-sm transition-colors ${form.cuenta_tipo === v ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-300 text-gray-700"}`}>
              <input type="radio" name="cuenta_tipo" value={v} checked={form.cuenta_tipo === v} onChange={() => set("cuenta_tipo", v)} className="accent-indigo-600" />
              {l}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {form.cuenta_tipo === "CELE" ? "Número de teléfono" : "Número de cuenta"}
        </label>
        <input
          type="tel"
          value={form.cuenta_numero}
          onChange={(e) => set("cuenta_numero", e.target.value.replace(/\D/g, ""))}
          placeholder={form.cuenta_tipo === "CELE" ? "04141234567" : "01340123456789012345"}
          required
          maxLength={20}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
        />
      </div>

      <div className="flex gap-3">
        <div className="w-28">
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo doc.</label>
          <select
            value={form.doc_tipo}
            onChange={(e) => set("doc_tipo", e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          >
            {["V", "J", "E", "G", "P"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Número de documento</label>
          <input
            type="text"
            value={form.doc_numero}
            onChange={(e) => set("doc_numero", e.target.value.replace(/\D/g, ""))}
            placeholder="12345678"
            required
            maxLength={12}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del titular</label>
        <input
          type="text"
          value={form.titular_nombre}
          onChange={(e) => set("titular_nombre", e.target.value)}
          placeholder="Juan Pérez"
          required
          maxLength={100}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
        />
      </div>

      <AlertaError mensaje={error} />

      <div className="flex gap-3 pt-1">
        <button
          type="submit"
          disabled={procesando}
          className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {procesando
            ? <><Spinner /> Guardando...</>
            : soloGuardar
              ? editando ? "Guardar cambios" : "Guardar método"
              : "Guardar y continuar →"
          }
        </button>
      </div>
    </form>
  );
}

// ── Paso 1b: Selección de moneda ─────────────────────────────────────────────

function SeleccionMoneda({ plan, precioInfo, onElegirVES, onElegirUSD, onCancelar, procesando, error, esVenezuela = false }) {
  const precioUSD = precioInfo?.precio_usd;
  const precioVES = precioInfo?.precio_ves;
  const tasa      = precioInfo?.tasa_bcv;
  const fechaTasa = precioInfo?.fecha_tasa;

  return (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-sm text-gray-600">Selecciona cómo deseas pagar el</p>
        <p className="text-xl font-bold text-gray-900 mt-1">Plan {plan?.label}</p>
        {precioUSD && (
          <p className="text-3xl font-bold text-indigo-600 mt-1">${precioUSD} <span className="text-base font-normal text-gray-400">USD/mes</span></p>
        )}
      </div>

      {tasa && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-xs text-gray-500 text-center">
          Tasa BCV del día: <strong className="text-gray-700">1 USD = {tasa.toLocaleString("es-VE", { minimumFractionDigits: 2 })} Bs</strong>
          {fechaTasa && <span className="ml-1">({new Date(fechaTasa).toLocaleDateString("es-VE")})</span>}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {esVenezuela && (
          <button
            onClick={onElegirVES}
            disabled={procesando || !precioVES}
            className="w-full border-2 border-indigo-200 hover:border-indigo-500 hover:bg-indigo-50 rounded-xl p-4 text-left transition-all disabled:opacity-50"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-800">Débito Inmediato</p>
                <p className="text-xs text-gray-500 mt-0.5">Bancario venezolano · SyPago</p>
              </div>
              <div className="text-right">
                {precioVES ? (
                  <p className="font-bold text-indigo-600">
                    {precioVES.toLocaleString("es-VE", { minimumFractionDigits: 2 })} Bs
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">Tasa no disponible</p>
                )}
              </div>
            </div>
            {procesando && <p className="text-xs text-indigo-500 mt-2">Enviando código OTP...</p>}
          </button>
        )}

        <button
          onClick={onElegirUSD}
          className="w-full border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 rounded-xl p-4 text-left transition-all"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-800">PayPal</p>
              <p className="text-xs text-gray-500 mt-0.5">Tarjeta de crédito/débito internacional</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-blue-600">${precioUSD} USD</p>
            </div>
          </div>
        </button>
      </div>

      <AlertaError mensaje={error} />

      <button onClick={onCancelar} className="w-full py-2.5 text-sm text-gray-500 hover:text-gray-700">
        Cancelar
      </button>
    </div>
  );
}

// ── Paso 2: Ingresar OTP ──────────────────────────────────────────────────────

function FormOtp({ plan, monto, moneda, onConfirmar, procesando, error }) {
  const [otp, setOtp] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirmar(otp);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-amber-800 mb-1">📱 Revisa tu teléfono</p>
        <p className="text-sm text-amber-700">
          Tu banco envió un código OTP por SMS para autorizar el cobro de{" "}
          <strong>{monto?.toLocaleString("es-VE")} {moneda}</strong> — Plan {plan}.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Código OTP recibido</label>
        <input
          type="text"
          inputMode="numeric"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          placeholder="12345678"
          required
          minLength={6}
          maxLength={10}
          autoFocus
          className="w-full border border-gray-300 rounded-lg px-3 py-3 text-xl font-mono text-center tracking-widest focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
        />
        <p className="text-xs text-gray-400 mt-1 text-center">Ingresa el código exactamente como lo recibiste</p>
      </div>

      <AlertaError mensaje={error} />

      <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
        <p>🔒 El código viaja cifrado directamente a tu banco. Nunca lo compartiremos.</p>
        <p>⏱ El código tiene validez limitada — ingrésalo cuanto antes.</p>
      </div>

      <button
        type="submit"
        disabled={procesando || otp.length < 6}
        className="w-full py-3 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {procesando ? <><Spinner /> Procesando pago...</> : "Confirmar pago"}
      </button>
    </form>
  );
}

// ── Paso 3: Resultado ─────────────────────────────────────────────────────────

function Resultado({ estado, plan, onCerrar, onReintentar }) {
  if (estado === "pendiente") {
    return (
      <div className="text-center space-y-4 py-4">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
        <p className="font-semibold text-gray-800">Esperando confirmación del banco...</p>
        <p className="text-sm text-gray-500">Esto puede tardar unos segundos. No cierres esta ventana.</p>
      </div>
    );
  }

  if (estado === "aprobado") {
    return (
      <div className="text-center space-y-4 py-4">
        <div className="text-5xl">✅</div>
        <p className="text-xl font-bold text-green-700">¡Pago confirmado!</p>
        <p className="text-sm text-gray-600">Tu plan <strong>{plan}</strong> está activo. Ya puedes usar todas sus funciones.</p>
        <button onClick={onCerrar} className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">
          Continuar
        </button>
      </div>
    );
  }

  if (estado === "rechazado") {
    return (
      <div className="text-center space-y-4 py-4">
        <div className="text-5xl">❌</div>
        <p className="text-xl font-bold text-red-700">Pago rechazado</p>
        <p className="text-sm text-gray-600">Tu banco rechazó el débito. Verifica que tienes saldo suficiente o intenta con otro método.</p>
        <button onClick={onCerrar} className="w-full py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700">
          Cerrar
        </button>
      </div>
    );
  }

  // Timeout — el pago puede haberse procesado pero el banco tardó en responder
  return (
    <div className="text-center space-y-4 py-4">
      <div className="text-5xl">⏳</div>
      <p className="text-xl font-bold text-amber-700">Verificando con tu banco...</p>
      <p className="text-sm text-gray-600">
        El banco está tardando en confirmar. El cobro puede haberse procesado correctamente.
      </p>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 text-left space-y-1">
        <p>• Revisa el SMS de tu banco para confirmar si el débito fue realizado.</p>
        <p>• Puedes cerrar esta ventana — tu plan se activará automáticamente si el pago fue aprobado.</p>
      </div>
      <div className="flex gap-3 pt-1">
        <button onClick={onReintentar} className="flex-1 py-2.5 border border-indigo-400 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-50">
          Verificar ahora
        </button>
        <button onClick={onCerrar} className="flex-1 py-2.5 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700">
          Cerrar
        </button>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function Suscripcion() {
  const { usuario } = useAuth();
  const esVenezuela = usuario?.moneda_local === "VES";
  const [uso, setUso] = useState(null);
  const [metodo, setMetodo] = useState(undefined); // undefined = cargando
  const [precios, setPrecios] = useState({});
  const [loading, setLoading] = useState(true);

  // Modal independiente para gestionar método de pago
  const [modalMetodo, setModalMetodo] = useState(false);
  const [guardandoMetodo, setGuardandoMetodo] = useState(false);
  const [errorMetodo, setErrorMetodo] = useState(null);

  // Estado del modal de pago
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [monedaSeleccionada, setMonedaSeleccionada] = useState(null); // "VES" | "USD"
  const [paso, setPaso] = useState(null); // "moneda" | "otp" | "resultado"
  const [cobroId, setCobroId] = useState(null);
  const [montoCobro, setMontoCobro] = useState(null);
  const [monedaCobro, setMonedaCobro] = useState("VES");
  const [estadoCobro, setEstadoCobro] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [errorModal, setErrorModal] = useState(null);

  const pollingRef = useRef(null);

  const cargarDatos = useCallback(() => {
    Promise.all([
      api.get("/suscripcion/"),
      api.get("/pagos/metodo-pago").catch(() => ({ data: null })),
      api.get("/pagos/precios").catch(() => ({ data: {} })),
    ]).then(([r1, r2, r3]) => {
      setUso(r1.data);
      setMetodo(r2.data);
      setPrecios(r3.data || {});
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    cargarDatos();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [cargarDatos]);

  const iniciarPolling = useCallback((id) => {
    let intentos = 0;
    pollingRef.current = setInterval(async () => {
      intentos++;
      try {
        const r = await api.get(`/pagos/cobros/${id}/estado`);
        const estado = r.data.estado;
        if (estado !== "pendiente") {
          setEstadoCobro(estado);
          clearInterval(pollingRef.current);
          if (estado === "aprobado") {
            api.get("/suscripcion/").then((r) => setUso(r.data));
          }
        }
      } catch { /* ignorar errores de red en polling */ }
      if (intentos >= 30) {
        clearInterval(pollingRef.current);
        setEstadoCobro("sin_respuesta");
      }
    }, 3000);
  }, []);

  const cerrarModal = () => {
    setPaso(null);
    setPlanSeleccionado(null);
    setMonedaSeleccionada(null);
    setCobroId(null);
    setMontoCobro(null);
    setEstadoCobro(null);
    setErrorModal(null);
    setProcesando(false);
    if (pollingRef.current) clearInterval(pollingRef.current);
  };

  // ── Gestionar método de pago (independiente del cobro) ──
  const handleGuardarMetodo = async (datos) => {
    setGuardandoMetodo(true);
    setErrorMetodo(null);
    try {
      const r = await api.post("/pagos/metodo-pago", datos);
      setMetodo(r.data);
      setModalMetodo(false);
    } catch (e) {
      setErrorMetodo(e.response?.data?.detail || "Error al guardar el método de pago");
    } finally {
      setGuardandoMetodo(false);
    }
  };

  const handleEliminarMetodo = async () => {
    if (!window.confirm("¿Eliminar el método de pago registrado?")) return;
    try {
      await api.delete("/pagos/metodo-pago");
      setMetodo(null);
    } catch { /* ignorar */ }
  };

  // ── Flujo de cobro ──
  const abrirPago = (plan) => {
    setPlanSeleccionado(plan);
    setErrorModal(null);
    setMonedaSeleccionada(null);
    setPaso("moneda");
  };

  const handleSolicitarOtp = async (plan, moneda = "VES") => {
    setProcesando(true);
    setErrorModal(null);
    try {
      const r = await api.post("/pagos/solicitar-otp", { plan, moneda });
      setCobroId(r.data.cobro_id);
      const precioInfo = precios[plan] || {};
      setMontoCobro(precioInfo.precio_ves || null);
      setMonedaCobro("VES");
      setPaso("otp");
    } catch (e) {
      setErrorModal(e.response?.data?.detail || "No se pudo enviar el OTP. Intenta nuevamente.");
    } finally {
      setProcesando(false);
    }
  };

  const handleConfirmarPago = async (otp) => {
    setProcesando(true);
    setErrorModal(null);
    try {
      await api.post("/pagos/confirmar-pago", { otp, cobro_id: cobroId });
      setEstadoCobro("pendiente");
      setPaso("resultado");
      iniciarPolling(cobroId);
    } catch (e) {
      setErrorModal(e.response?.data?.detail || "OTP incorrecto o pago rechazado por el banco");
    } finally {
      setProcesando(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );

  const planInfo = PLANES.find((p) => p.id === uso?.plan) || PLANES[0];
  const enLimiteProd = uso?.productos?.limite && uso.productos.usado >= uso.productos.limite;
  const enLimiteCat  = uso?.categorias?.limite && uso.categorias.usado >= uso.categorias.limite;
  const enLimiteUser = uso?.usuarios?.limite   && uso.usuarios.usado   >= uso.usuarios.limite;
  const hayLimiteAlcanzado = enLimiteProd || enLimiteCat || enLimiteUser;

  const tituloPaso = {
    moneda:    `Contratar Plan ${planSeleccionado ? PLANES.find(p => p.id === planSeleccionado)?.label : ""}`,
    otp:       `Pagar con Débito Inmediato`,
    resultado: "Resultado del pago",
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Suscripción</h2>
        <p className="text-gray-500 text-sm mt-1">Estado actual de tu plan y uso de recursos</p>
      </div>

      {/* Plan actual */}
      <div className={`rounded-xl border-2 p-5 ${hayLimiteAlcanzado ? "border-red-300 bg-red-50" : "border-indigo-200 bg-indigo-50"}`}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Plan actual</p>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold capitalize" style={{ color: planInfo.colorTexto }}>{planInfo.label}</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: planInfo.colorBadge, color: planInfo.colorTexto }}>
                {uso?.activa ? "Activa" : "Inactiva"}
              </span>
            </div>
            {uso?.fecha_fin && (
              <p className="text-xs text-gray-500 mt-1">Vence el {new Date(uso.fecha_fin).toLocaleDateString("es-VE")}</p>
            )}
          </div>
          {hayLimiteAlcanzado && (
            <div className="bg-red-100 border border-red-300 rounded-lg px-3 py-2 text-sm text-red-700 font-medium">
              Límite alcanzado — considera actualizar tu plan
            </div>
          )}
        </div>
      </div>

      {/* Medidores de uso */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-5">
        <h3 className="font-semibold text-gray-800">Uso de recursos</h3>
        <BarraUso label="Productos"  usado={uso?.productos?.usado  ?? 0} limite={uso?.productos?.limite}  color="#6366f1" />
        <BarraUso label="Categorías" usado={uso?.categorias?.usado ?? 0} limite={uso?.categorias?.limite} color="#8b5cf6" />
        <BarraUso label="Usuarios"   usado={uso?.usuarios?.usado   ?? 0} limite={uso?.usuarios?.limite}   color="#06b6d4" />
      </div>

      {/* Método de pago registrado — solo Venezuela */}
      {esVenezuela && <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">Método de pago</h3>
          <button
            onClick={() => { setErrorMetodo(null); setModalMetodo(true); }}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            {metodo ? "Cambiar" : "+ Agregar"}
          </button>
        </div>
        {metodo ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800">{metodo.titular_nombre}</p>
              <p className="text-sm text-gray-500">
                {BANCOS.find(b => b.codigo === metodo.banco_codigo)?.nombre || metodo.banco_codigo}
                {" — "}{metodo.cuenta_tipo === "CELE" ? "Teléfono" : "Cuenta"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">🔒 Cifrado</span>
              <button onClick={handleEliminarMetodo} className="text-xs text-red-400 hover:text-red-600">Eliminar</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Sin método registrado. Agrégalo para poder suscribirte a un plan.</p>
        )}
      </div>}

      {/* Planes disponibles */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-4">Planes disponibles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANES.map((plan) => {
            const esCurrent = uso?.plan === plan.id;
            const precioInfo = precios[plan.id];
            const precioLabel = plan.id === "gratis"
              ? "Gratis"
              : precioInfo?.precio_usd
                ? `$${precioInfo.precio_usd} USD/mes`
                : "—";
            const precioVESLabel = precioInfo?.precio_ves
              ? `≈ ${precioInfo.precio_ves.toLocaleString("es-VE", { minimumFractionDigits: 0 })} Bs`
              : null;

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-xl p-6 relative flex flex-col ${plan.popular ? "shadow-lg" : "shadow-sm"}`}
                style={{ border: `2px solid ${esCurrent ? plan.colorBorde : "#e5e7eb"}` }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Más popular</span>
                  </div>
                )}

                <div className="mb-4">
                  <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ background: plan.colorBadge, color: plan.colorTexto }}>
                    {plan.label}
                  </span>
                  <div className="mt-3">
                    <span className="text-2xl font-bold text-gray-900">{precioLabel}</span>
                    {esVenezuela && precioVESLabel && (
                      <p className="text-xs text-gray-400 mt-0.5">{precioVESLabel} al cambio del día</p>
                    )}
                  </div>
                </div>

                <ul className="space-y-1.5 mb-5 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>{f}
                    </li>
                  ))}
                  {plan.noFeatures?.map((f, i) => (
                    <li key={`no-${i}`} className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="mt-0.5 flex-shrink-0">✕</span>{f}
                    </li>
                  ))}
                </ul>

                {esCurrent ? (
                  <div className="w-full text-center py-2.5 border-2 border-green-500 text-green-700 rounded-lg text-sm font-medium bg-green-50">
                    Plan actual
                  </div>
                ) : plan.id === "gratis" ? (
                  <div className="w-full text-center py-2.5 border border-gray-200 text-gray-400 rounded-lg text-sm">
                    Plan base gratuito
                  </div>
                ) : (
                  <button
                    onClick={() => abrirPago(plan.id)}
                    className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${plan.popular ? "bg-indigo-600 text-white hover:bg-indigo-700" : "border border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                  >
                    Contratar {plan.label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {esVenezuela && (
        <p className="text-center text-xs text-gray-400">
          🔒 Los pagos se procesan por débito inmediato SyPago. Tus datos bancarios están cifrados.
        </p>
      )}


      {/* Modal independiente: gestionar método de pago */}
      {modalMetodo && (
        <Modal titulo={metodo ? "Editar método de pago" : "Agregar método de pago"} onCerrar={() => setModalMetodo(false)}>
          <FormMetodoPago
            metodoExistente={metodo}
            onUsar={null}
            onRegistrar={handleGuardarMetodo}
            procesando={guardandoMetodo}
            error={errorMetodo}
            soloGuardar
          />
        </Modal>
      )}

      {/* Modal de pago */}
      {paso && (
        <Modal titulo={tituloPaso[paso] || "Pago"} onCerrar={paso === "resultado" && estadoCobro === "pendiente" ? undefined : cerrarModal}>
          {paso === "moneda" && (
            <SeleccionMoneda
              plan={PLANES.find(p => p.id === planSeleccionado)}
              precioInfo={precios[planSeleccionado]}
              esVenezuela={esVenezuela}
              onElegirVES={() => {
                setMonedaSeleccionada("VES");
                if (!metodo) { cerrarModal(); setModalMetodo(true); return; }
                handleSolicitarOtp(planSeleccionado, "VES");
              }}
              onElegirUSD={() => {
                setMonedaSeleccionada("USD");
                alert("Pago con PayPal próximamente. Por ahora usa Débito Inmediato en bolívares.");
              }}
              onCancelar={cerrarModal}
              procesando={procesando}
              error={errorModal}
            />
          )}

          {paso === "otp" && (
            <FormOtp
              plan={PLANES.find(p => p.id === planSeleccionado)?.label}
              monto={montoCobro}
              moneda={monedaCobro}
              onConfirmar={handleConfirmarPago}
              procesando={procesando}
              error={errorModal}
            />
          )}

          {paso === "resultado" && (
            <Resultado
              estado={estadoCobro}
              plan={PLANES.find(p => p.id === planSeleccionado)?.label}
              onCerrar={cerrarModal}
              onReintentar={async () => {
                if (!cobroId) return;
                setEstadoCobro("pendiente");
                try {
                  const r = await api.get(`/pagos/cobros/${cobroId}/estado`);
                  setEstadoCobro(r.data.estado);
                  if (r.data.estado === "aprobado") {
                    api.get("/suscripcion/").then(r => setUso(r.data));
                  }
                } catch {
                  setEstadoCobro("sin_respuesta");
                }
              }}
            />
          )}
        </Modal>
      )}
    </div>
  );
}
